import { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'react-hot-toast';
import { format } from 'date-fns';

const MONTHS = [
  { value: 1, label: 'Jan' },
  { value: 2, label: 'Fev' },
  { value: 3, label: 'Mar' },
  { value: 4, label: 'Abr' },
  { value: 5, label: 'Mai' },
  { value: 6, label: 'Jun' },
  { value: 7, label: 'Jul' },
  { value: 8, label: 'Ago' },
  { value: 9, label: 'Set' },
  { value: 10, label: 'Out' },
  { value: 11, label: 'Nov' },
  { value: 12, label: 'Dez' },
];

const OrcadoRealizado = () => {
  const now = new Date();
  const anoAtual = Number(format(now, 'yyyy'));
  const mesAtual = Number(format(now, 'M'));

  const [carregando, setCarregando] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [centrosCustos, setCentrosCustos] = useState([]);
  const [contasBancarias, setContasBancarias] = useState([]);

  const [filtros, setFiltros] = useState({
    ano: anoAtual,
    mes_inicio: 1,
    mes_fim: mesAtual,
    regime: 'competencia',
    centro_custo_id: 'todos',
    conta_bancaria_id: 'todas',
  });
  const [mesOrcamento, setMesOrcamento] = useState(mesAtual);

  const [dados, setDados] = useState({ linhas: [], totais: {} });
  const [orcamentoItens, setOrcamentoItens] = useState([]);
  const [orcamentoDraft, setOrcamentoDraft] = useState({});

  const formatarValor = (valor) =>
    Number(valor || 0).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

  const carregarFiltros = async () => {
    try {
      const [centrosResponse, contasResponse] = await Promise.all([
        axios.get('/api/centro-custos'),
        axios.get('/api/contas-bancarias'),
      ]);
      setCentrosCustos(Array.isArray(centrosResponse.data) ? centrosResponse.data : []);
      setContasBancarias(Array.isArray(contasResponse.data) ? contasResponse.data : []);
    } catch (error) {
      console.error('Erro ao carregar filtros de orcado vs realizado:', error);
    }
  };

  const carregarComparativo = async () => {
    setCarregando(true);
    try {
      const response = await axios.get('/api/orcado-realizado', { params: filtros });
      const payload = response.data || {};
      setDados({
        linhas: Array.isArray(payload.linhas) ? payload.linhas : [],
        totais: payload.totais || {},
      });
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar Orcado vs Realizado');
    } finally {
      setCarregando(false);
    }
  };

  const carregarOrcamentoMes = async () => {
    try {
      const response = await axios.get('/api/orcamentos-mensais', {
        params: {
          ano: filtros.ano,
          mes: mesOrcamento,
          centro_custo_id: filtros.centro_custo_id,
        },
      });
      const itens = Array.isArray(response.data?.itens) ? response.data.itens : [];
      const draft = {};
      itens.forEach((item) => {
        draft[item.plano_conta_id] = Number(item.valor_orcado || 0);
      });
      setOrcamentoItens(itens);
      setOrcamentoDraft(draft);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao carregar orcamento mensal');
    }
  };

  const salvarOrcamentoMes = async () => {
    setSalvando(true);
    try {
      const itens = orcamentoItens.map((item) => ({
        plano_conta_id: item.plano_conta_id,
        valor_orcado: Number(orcamentoDraft[item.plano_conta_id] || 0),
      }));

      await axios.put('/api/orcamentos-mensais', {
        ano: filtros.ano,
        mes: mesOrcamento,
        centro_custo_id: filtros.centro_custo_id,
        itens,
      });
      toast.success('Orcamento mensal salvo');
      await Promise.all([carregarOrcamentoMes(), carregarComparativo()]);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Erro ao salvar orcamento mensal');
    } finally {
      setSalvando(false);
    }
  };

  useEffect(() => {
    carregarFiltros();
  }, []);

  useEffect(() => {
    carregarComparativo();
  }, [filtros]);

  useEffect(() => {
    carregarOrcamentoMes();
  }, [filtros.ano, filtros.centro_custo_id, mesOrcamento]);

  const resumo = useMemo(() => {
    const t = dados.totais || {};
    return {
      receitasOrcadas: Number(t.orcado_receitas || 0),
      receitasRealizadas: Number(t.realizado_receitas || 0),
      despesasOrcadas: Number(t.orcado_despesas || 0),
      despesasRealizadas: Number(t.realizado_despesas || 0),
      resultadoOrcado: Number(t.orcado_resultado || 0),
      resultadoRealizado: Number(t.realizado_resultado || 0),
      desvioResultado: Number(t.desvio_resultado || 0),
    };
  }, [dados.totais]);

  return (
    <div className="animate-fade-in">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Orcado vs Realizado</h1>
          <p className="text-gray-600">Desvio absoluto e percentual por conta</p>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-body">
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <div>
              <label className="form-label">Ano</label>
              <input
                type="number"
                min="2000"
                max="2100"
                className="form-input"
                value={filtros.ano}
                onChange={(e) => setFiltros((old) => ({ ...old, ano: Number(e.target.value || anoAtual) }))}
              />
            </div>
            <div>
              <label className="form-label">Mes Inicial</label>
              <select
                className="form-select"
                value={filtros.mes_inicio}
                onChange={(e) =>
                  setFiltros((old) => {
                    const nextStart = Number(e.target.value);
                    return {
                      ...old,
                      mes_inicio: nextStart,
                      mes_fim: old.mes_fim < nextStart ? nextStart : old.mes_fim,
                    };
                  })
                }
              >
                {MONTHS.map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Mes Final</label>
              <select
                className="form-select"
                value={filtros.mes_fim}
                onChange={(e) => setFiltros((old) => ({ ...old, mes_fim: Number(e.target.value) }))}
              >
                {MONTHS.filter((month) => month.value >= filtros.mes_inicio).map((month) => (
                  <option key={month.value} value={month.value}>
                    {month.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Regime</label>
              <select
                className="form-select"
                value={filtros.regime}
                onChange={(e) => setFiltros((old) => ({ ...old, regime: e.target.value }))}
              >
                <option value="competencia">Competencia</option>
                <option value="caixa">Caixa</option>
              </select>
            </div>
            <div>
              <label className="form-label">Centro de Custo</label>
              <select
                className="form-select"
                value={filtros.centro_custo_id}
                onChange={(e) => setFiltros((old) => ({ ...old, centro_custo_id: e.target.value }))}
              >
                <option value="todos">Todos</option>
                {centrosCustos.map((centro) => (
                  <option key={centro.id} value={centro.id}>
                    {centro.codigo} - {centro.descricao}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="form-label">Conta Bancaria</label>
              <select
                className="form-select"
                value={filtros.conta_bancaria_id}
                onChange={(e) => setFiltros((old) => ({ ...old, conta_bancaria_id: e.target.value }))}
              >
                <option value="todas">Todas</option>
                {contasBancarias.map((conta) => (
                  <option key={conta.id} value={conta.id}>
                    {conta.banco} - {conta.conta}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Receitas</p>
            <p className="text-xs text-gray-500">Orcado: R$ {formatarValor(resumo.receitasOrcadas)}</p>
            <p className="text-lg font-bold text-success-700">Realizado: R$ {formatarValor(resumo.receitasRealizadas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Despesas</p>
            <p className="text-xs text-gray-500">Orcado: R$ {formatarValor(resumo.despesasOrcadas)}</p>
            <p className="text-lg font-bold text-danger-700">Realizado: R$ {formatarValor(resumo.despesasRealizadas)}</p>
          </div>
        </div>
        <div className="card">
          <div className="card-body">
            <p className="text-sm text-gray-500">Resultado</p>
            <p className="text-xs text-gray-500">Orcado: R$ {formatarValor(resumo.resultadoOrcado)}</p>
            <p className={`text-lg font-bold ${resumo.resultadoRealizado >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              Realizado: R$ {formatarValor(resumo.resultadoRealizado)}
            </p>
            <p className={`text-xs ${resumo.desvioResultado >= 0 ? 'text-success-700' : 'text-danger-700'}`}>
              Desvio: R$ {formatarValor(resumo.desvioResultado)}
            </p>
          </div>
        </div>
      </div>

      <div className="card mb-6">
        <div className="card-header flex items-center justify-between">
          <h3 className="text-lg font-semibold">Orcamento Mensal (base para comparativo)</h3>
          <div className="flex items-center gap-2">
            <select className="form-select" value={mesOrcamento} onChange={(e) => setMesOrcamento(Number(e.target.value))}>
              {MONTHS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <button className="btn-primary" onClick={salvarOrcamentoMes} disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar Orcamento'}
            </button>
          </div>
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th>Valor Orcado</th>
                </tr>
              </thead>
              <tbody>
                {orcamentoItens.map((item) => (
                  <tr key={item.plano_conta_id}>
                    <td>{item.codigo} - {item.descricao}</td>
                    <td className={item.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>{item.tipo}</td>
                    <td>
                      <input
                        type="number"
                        step="0.01"
                        className="form-input max-w-[180px]"
                        value={orcamentoDraft[item.plano_conta_id] ?? 0}
                        onChange={(e) =>
                          setOrcamentoDraft((old) => ({
                            ...old,
                            [item.plano_conta_id]: Number(e.target.value || 0),
                          }))
                        }
                      />
                    </td>
                  </tr>
                ))}
                {orcamentoItens.length === 0 && (
                  <tr>
                    <td colSpan={3} className="text-center text-gray-500 py-6">Nenhuma conta encontrada</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header flex justify-between items-center">
          <h3 className="text-lg font-semibold">Comparativo por Conta</h3>
          {carregando && <div className="loader h-5 w-5" />}
        </div>
        <div className="card-body p-0">
          <div className="table-container">
            <table className="table">
              <thead>
                <tr>
                  <th>Conta</th>
                  <th>Tipo</th>
                  <th>Orcado</th>
                  <th>Realizado</th>
                  <th>Desvio Abs.</th>
                  <th>Desvio %</th>
                </tr>
              </thead>
              <tbody>
                {(dados.linhas || []).map((linha) => (
                  <tr key={linha.plano_conta_id}>
                    <td>{linha.codigo} - {linha.descricao}</td>
                    <td className={linha.tipo === 'receita' ? 'text-success-700' : 'text-danger-700'}>{linha.tipo}</td>
                    <td>R$ {formatarValor(linha.valor_orcado)}</td>
                    <td>R$ {formatarValor(linha.valor_realizado)}</td>
                    <td className={Number(linha.desvio_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>
                      R$ {formatarValor(linha.desvio_abs)}
                    </td>
                    <td className={Number(linha.desvio_abs || 0) >= 0 ? 'text-success-700' : 'text-danger-700'}>
                      {linha.desvio_pct == null ? '-' : `${Number(linha.desvio_pct).toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`}
                    </td>
                  </tr>
                ))}
                {(dados.linhas || []).length === 0 && !carregando && (
                  <tr>
                    <td colSpan={6} className="text-center text-gray-500 py-8">
                      Nenhum dado encontrado para os filtros selecionados
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
};

export default OrcadoRealizado;
